import { useState, useEffect, useMemo } from 'react'
import { Container, Row, Col, Alert, Modal, Button, Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { getEvents } from '../../../actions/calendarActions'
import Loader from '../../../components/loaders/Loader'

const HOURS = Array.from({ length: 13 }, (_, i) => i + 8)

function getMonday(d) {
    const date = new Date(d)
    const day = date.getDay()
    const diff = date.getDate() - day + (day === 0 ? -6 : 1)
    date.setDate(diff)
    date.setHours(0, 0, 0, 0)
    return date
}

function formatHour(h) {
    return `${String(h).padStart(2, '0')}:00`
}

function getWeekDates(monday) {
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday)
        d.setDate(monday.getDate() + i)
        return d
    })
}

function getMonthDates(year, month) {
    const first = new Date(year, month, 1)
    const last = new Date(year, month + 1, 0)
    const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1
    const dates = []
    for (let i = -startDay; i < 42 - startDay; i++) {
        const d = new Date(year, month, 1 + i)
        dates.push(d)
    }
    return dates
}

function parseTime(timeStr) {
    if (!timeStr) return null
    const [h, m] = timeStr.split(':').map(Number)
    return h + (m || 0) / 60
}

function getEventColors(event) {
    if (event.isSubstitute) return { bg: '#e0f2fe', border: '#0ea5e9', text: '#0c4a6e' }
    if (event.state === 'done') return { bg: '#dcfce7', border: '#22c55e', text: '#166534' }
    if (event.state === 'in_progress' || event.state === 'open') return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' }
    if (event.isOnline) return { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' }
    return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' }
}

export default function CalendarView() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { events, loading, calendarError } = useSelector(state => state.calendar)
    const [view, setView] = useState('weekly')
    const [currentMonday, setCurrentMonday] = useState(getMonday(new Date()))
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth())
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
    const [currentDay, setCurrentDay] = useState(new Date())
    const [selectedEvent, setSelectedEvent] = useState(null)

    const weekDates = getWeekDates(currentMonday)
    const todayStr = new Date().toISOString().split('T')[0]

    const ROW_HEIGHT = 72
    const HOUR_COL = 56

    const hours = useMemo(() => {
        let minH = 8
        let maxH = 20
        ;(events || []).forEach(e => {
            const st = parseTime(e.startTime)
            const en = parseTime(e.endTime)
            if (st !== null && Math.floor(st) < minH) minH = Math.floor(st)
            if (en !== null && Math.ceil(en) > maxH) maxH = Math.ceil(en)
        })
        return Array.from({ length: maxH - minH + 1 }, (_, i) => i + minH)
    }, [events])

    useEffect(() => {
        if (view === 'daily') {
            const dayStr = currentDay.toISOString().split('T')[0]
            dispatch(getEvents({ startDate: dayStr, endDate: dayStr }))
        } else if (view === 'weekly') {
            const monday = new Date(currentMonday)
            const sunday = new Date(monday)
            sunday.setDate(monday.getDate() + 6)
            dispatch(getEvents({
                startDate: monday.toISOString().split('T')[0],
                endDate: sunday.toISOString().split('T')[0]
            }))
        } else {
            const first = new Date(currentYear, currentMonth, 1)
            const last = new Date(currentYear, currentMonth + 1, 0)
            dispatch(getEvents({
                startDate: first.toISOString().split('T')[0],
                endDate: last.toISOString().split('T')[0]
            }))
        }
    }, [dispatch, currentDay, currentMonday, currentMonth, currentYear, view])

    if (loading && (!events || events.length === 0)) return <Loader />

    if (calendarError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{calendarError}</Alert>
        </Container>
    )

    const prev = () => {
        if (view === 'daily') {
            const d = new Date(currentDay)
            d.setDate(d.getDate() - 1)
            setCurrentDay(d)
        } else if (view === 'weekly') {
            const d = new Date(currentMonday)
            d.setDate(d.getDate() - 7)
            setCurrentMonday(d)
        } else {
            if (currentMonth === 0) {
                setCurrentMonth(11)
                setCurrentYear(y => y - 1)
            } else {
                setCurrentMonth(m => m - 1)
            }
        }
    }

    const next = () => {
        if (view === 'daily') {
            const d = new Date(currentDay)
            d.setDate(d.getDate() + 1)
            setCurrentDay(d)
        } else if (view === 'weekly') {
            const d = new Date(currentMonday)
            d.setDate(d.getDate() + 7)
            setCurrentMonday(d)
        } else {
            if (currentMonth === 11) {
                setCurrentMonth(0)
                setCurrentYear(y => y + 1)
            } else {
                setCurrentMonth(m => m + 1)
            }
        }
    }

    const goToday = () => {
        setCurrentDay(new Date())
        setCurrentMonday(getMonday(new Date()))
        setCurrentMonth(new Date().getMonth())
        setCurrentYear(new Date().getFullYear())
    }

    const periodLabel = () => {
        if (view === 'daily') {
            return currentDay.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
        }
        if (view === 'weekly') {
            const start = weekDates[0]
            const end = weekDates[6]
            const fmt = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
            return `${fmt(start)} - ${fmt(end)} ${end.getFullYear()}`
        }
        return `${[t('months.1'), t('months.2'), t('months.3'), t('months.4'), t('months.5'), t('months.6'), t('months.7'), t('months.8'), t('months.9'), t('months.10'), t('months.11'), t('months.12')][currentMonth]} ${currentYear}`
    }

    return (
        <Container fluid className='px-4 py-3' style={{ maxWidth: '1400px' }}>
            <Row className='align-items-center mb-3'>
                <Col>
                    <h3 className='fw-bold mb-1'>{t('teacher.calendar.title')}</h3>
                    <p className='text-muted mb-0'>{t('teacher.calendar.subtitle')}</p>
                </Col>
            </Row>

            <Row className='align-items-center mb-3'>
                <Col>
                    <div className='d-flex align-items-center gap-2'>
                        <button onClick={goToday} className='btn btn-sm btn-outline-secondary rounded-pill px-3'>{t('teacher.calendar.today')}</button>
                        <button onClick={prev} className='btn btn-sm btn-outline-secondary rounded-circle' style={{ width: '32px', height: '32px' }}>
                            <i className='fa-solid fa-chevron-left'></i>
                        </button>
                        <button onClick={next} className='btn btn-sm btn-outline-secondary rounded-circle' style={{ width: '32px', height: '32px' }}>
                            <i className='fa-solid fa-chevron-right'></i>
                        </button>
                        <span className='fw-semibold ms-2' style={{ fontSize: '15px' }}>{periodLabel()}</span>
                    </div>
                </Col>
                <Col xs='auto' className='d-flex gap-2 align-items-center'>
                    <Link to='/teacher/schedule' className='btn btn-sm btn-outline-secondary rounded-pill px-3' style={{ fontSize: '13px' }}>
                        {t('teacher.calendar.schedule')}
                    </Link>
                    <div className='d-flex gap-1 bg-light rounded-pill p-1'>
                        <button onClick={() => setView('daily')}
                            className={`btn btn-sm rounded-pill px-3 ${view === 'daily' ? 'bg-white shadow-sm fw-semibold' : 'bg-transparent text-muted'}`}
                            style={{ fontSize: '13px' }}>
                            {t('teacher.calendar.daily')}
                        </button>
                        <button onClick={() => setView('weekly')}
                            className={`btn btn-sm rounded-pill px-3 ${view === 'weekly' ? 'bg-white shadow-sm fw-semibold' : 'bg-transparent text-muted'}`}
                            style={{ fontSize: '13px' }}>
                            {t('teacher.calendar.weekly')}
                        </button>
                        <button onClick={() => setView('monthly')}
                            className={`btn btn-sm rounded-pill px-3 ${view === 'monthly' ? 'bg-white shadow-sm fw-semibold' : 'bg-transparent text-muted'}`}
                            style={{ fontSize: '13px' }}>
                            {t('teacher.calendar.monthly')}
                        </button>
                    </div>
                </Col>
            </Row>

            {view === 'daily' && (
                <Row>
                    <Col>
                        <div className='border rounded-4 bg-white shadow-sm p-3'>
                            {(() => {
                                const dayStr = currentDay.toISOString().split('T')[0]
                                const dayEvents = (events || [])
                                    .filter(e => e.startDate === dayStr)
                                    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
                                if (dayEvents.length === 0) {
                                    return <p className='text-muted text-center py-4 mb-0'>{t('teacher.calendar.noclasses')}</p>
                                }
                                return dayEvents.map((evt, ei) => {
                                    const colors = getEventColors(evt)
                                    return (
                                        <div key={ei} onClick={() => setSelectedEvent(evt)}
                                            className='d-flex align-items-center gap-3 p-3 mb-2 rounded-3'
                                            style={{ backgroundColor: colors.bg, borderLeft: `4px solid ${colors.border}`, cursor: 'pointer' }}>
                                            <div className='fw-bold flex-shrink-0' style={{ fontSize: '14px', color: colors.text, minWidth: '110px' }}>
                                                {evt.startTime} - {evt.endTime}
                                            </div>
                                            <div className='flex-grow-1'>
                                                <p className='fw-semibold mb-0' style={{ fontSize: '15px', color: colors.text }}>{evt.groupName}</p>
                                                <p className='mb-0' style={{ fontSize: '12px', color: colors.text, opacity: 0.8 }}>
                                                    {[evt.courseName, evt.classroom, evt.center].filter(Boolean).join(' · ')}
                                                </p>
                                            </div>
                                            {evt.isSubstitute && (
                                                <Badge bg='info' className='rounded-pill flex-shrink-0'>{t('teacher.calendar.substitutions')}</Badge>
                                            )}
                                            <i className='fa-solid fa-chevron-right flex-shrink-0' style={{ color: colors.border }}></i>
                                        </div>
                                    )
                                })
                            })()}
                        </div>
                    </Col>
                </Row>
            )}

            {view === 'weekly' && (
                <Row>
                    <Col>
                        <div className='border rounded-4 bg-white shadow-sm overflow-hidden'>
                            <div style={{ display: 'grid', gridTemplateColumns: `${HOUR_COL}px repeat(7, 1fr)` }}>
                                <div style={{ borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}></div>
                                {weekDates.map((d, i) => {
                                    const dateStr = d.toISOString().split('T')[0]
                                    const isToday = dateStr === todayStr
                                    return (
                                        <div key={i} style={{
                                            borderRight: i < 6 ? '1px solid #e5e7eb' : 'none',
                                            borderBottom: '1px solid #e5e7eb',
                                            padding: '8px 4px',
                                            textAlign: 'center',
                                            backgroundColor: isToday ? '#eff6ff' : '#fafafa'
                                        }}>
                                            <p className='mb-0' style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{[t('teacher.calendar.mon'), t('teacher.calendar.tue'), t('teacher.calendar.wed'), t('teacher.calendar.thu'), t('teacher.calendar.fri'), t('teacher.calendar.sat'), t('teacher.calendar.sun')][i]}</p>
                                            <p className='mb-0 fw-bold' style={{ fontSize: '20px', color: isToday ? '#3b82f6' : '#111827' }}>{d.getDate()}</p>
                                        </div>
                                    )
                                })}

                                {hours.map(hour => (
                                    <div key={hour} style={{ display: 'contents' }}>
                                        <div style={{
                                            borderRight: '1px solid #e5e7eb',
                                            borderBottom: '1px solid #f3f4f6',
                                            height: `${ROW_HEIGHT}px`,
                                            padding: '0 6px',
                                            fontSize: '11px',
                                            color: '#9ca3af',
                                            textAlign: 'right',
                                            position: 'relative'
                                        }}>
                                            <span style={{ position: 'absolute', top: '-7px', right: '6px' }}>{formatHour(hour)}</span>
                                        </div>
                                        {weekDates.map((d, di) => {
                                            const dateStr = d.toISOString().split('T')[0]
                                            const isToday = dateStr === todayStr
                                            const hourEvents = (events || []).filter(e => {
                                                if (e.startDate !== dateStr) return false
                                                const st = parseTime(e.startTime)
                                                return st !== null && Math.floor(st) === hour
                                            })

                                            return (
                                                <div key={di} style={{
                                                    borderRight: di < 6 ? '1px solid #e5e7eb' : 'none',
                                                    borderBottom: '1px solid #f3f4f6',
                                                    position: 'relative',
                                                    backgroundColor: isToday ? '#f8fbff' : '#fff',
                                                    height: `${ROW_HEIGHT}px`
                                                }}>
                                                    {hourEvents.map((evt, ei) => {
                                                        const st = parseTime(evt.startTime)
                                                        const en = parseTime(evt.endTime) || st + 1
                                                        const minutesIntoHour = (st - hour) * 60
                                                        const durationMin = Math.max((en - st) * 60, 20)
                                                        const colors = getEventColors(evt)

                                                        return (
                                                            <div key={ei}
                                                                title={`${evt.startTime} - ${evt.endTime}\n${evt.groupName}\n${evt.classroom || ''}`}
                                                                onClick={() => setSelectedEvent(evt)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    left: '3px',
                                                                    right: '3px',
                                                                    top: `${(minutesIntoHour / 60) * ROW_HEIGHT}px`,
                                                                    height: `${Math.max((durationMin / 60) * ROW_HEIGHT, 24)}px`,
                                                                    backgroundColor: colors.bg,
                                                                    borderLeft: `3px solid ${colors.border}`,
                                                                    borderRadius: '6px',
                                                                    padding: '4px 6px',
                                                                    overflow: 'hidden',
                                                                    zIndex: 1,
                                                                    cursor: 'pointer',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                                                }}>
                                                                <p className='mb-0 fw-bold' style={{ fontSize: '12px', lineHeight: '1.2', color: colors.text }}>
                                                                    {evt.startTime} - {evt.endTime}
                                                                </p>
                                                                <p className='mb-0 fw-semibold' style={{ fontSize: '11px', lineHeight: '1.2', color: colors.text }}>
                                                                    {evt.groupName}
                                                                </p>
                                                                {evt.classroom && (
                                                                    <p className='mb-0' style={{ fontSize: '10px', color: colors.text, opacity: 0.75 }}>
                                                                        <i className='fa-solid fa-location-dot me-1'></i>{evt.classroom}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {view === 'monthly' && (
                <Row>
                    <Col>
                        <div className='border rounded-4 bg-white shadow-sm overflow-hidden'>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                                {[t('teacher.calendar.mon'), t('teacher.calendar.tue'), t('teacher.calendar.wed'), t('teacher.calendar.thu'), t('teacher.calendar.fri'), t('teacher.calendar.sat'), t('teacher.calendar.sun')].map((day, i) => (
                                    <div key={i} style={{
                                        borderBottom: '1px solid #e5e7eb',
                                        padding: '8px 4px',
                                        textAlign: 'center',
                                        backgroundColor: '#fafafa',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#6b7280',
                                        textTransform: 'uppercase'
                                    }}>
                                        {day}
                                    </div>
                                ))}

                                {getMonthDates(currentYear, currentMonth).map((d, i) => {
                                    const dateStr = d.toISOString().split('T')[0]
                                    const isCurrentMonth = d.getMonth() === currentMonth
                                    const isToday = dateStr === todayStr
                                    const dayEvents = (events || []).filter(e => e.startDate === dateStr)

                                    return (
                                        <div key={i} style={{
                                            borderBottom: '1px solid #f3f4f6',
                                            borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid #f3f4f6',
                                            minHeight: '100px',
                                            padding: '4px',
                                            backgroundColor: isToday ? '#f0f7ff' : isCurrentMonth ? '#fff' : '#f9fafb',
                                            opacity: isCurrentMonth ? 1 : 0.5
                                        }}>
                                            <p className='mb-1' style={{
                                                fontSize: '13px',
                                                fontWeight: isToday ? 700 : 500,
                                                color: isToday ? '#3b82f6' : '#374151',
                                                textAlign: 'right',
                                                paddingRight: '4px'
                                            }}>
                                                {isToday && <span className='d-inline-flex align-items-center justify-content-center rounded-circle me-1' style={{ width: '20px', height: '20px', backgroundColor: '#3b82f6', color: '#fff', fontSize: '11px', fontWeight: 700 }}>{d.getDate()}</span>}
                                                {!isToday && d.getDate()}
                                            </p>
                                            <div className='d-flex flex-column gap-1'>
                                                {dayEvents.slice(0, 3).map((evt, ei) => {
                                                    const colors = getEventColors(evt)
                                                    return (
                                                        <div key={ei} onClick={() => setSelectedEvent(evt)} style={{
                                                            backgroundColor: colors.bg,
                                                            borderLeft: `3px solid ${colors.border}`,
                                                            borderRadius: '4px',
                                                            padding: '2px 4px',
                                                            fontSize: '10px',
                                                            lineHeight: '1.3',
                                                            color: colors.text,
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                            textOverflow: 'ellipsis',
                                                            cursor: 'pointer'
                                                        }}>
                                                            <span className='fw-bold'>{evt.startTime}</span> {evt.groupName}
                                                            {evt.isSubstitute && <span className='ms-1' style={{ fontSize: '9px' }}>&#9733;</span>}
                                                        </div>
                                                    )
                                                })}
                                                {dayEvents.length > 3 && (
                                                    <p className='mb-0 text-muted' style={{ fontSize: '10px', textAlign: 'center' }}>
                                                        +{dayEvents.length - 3} {t('teacher.calendar.more')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            <Row className='mt-3'>
                <Col className='d-flex gap-3 flex-wrap'>
                    <div className='d-flex align-items-center gap-2'>
                        <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#dbeafe', borderLeft: '3px solid #3b82f6' }}></div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('teacher.calendar.legendScheduled')}</span>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#e0f2fe', borderLeft: '3px solid #0ea5e9' }}></div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('teacher.calendar.legendSubstitute')}</span>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#fef3c7', borderLeft: '3px solid #f59e0b' }}></div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('teacher.calendar.legendInProgress')}</span>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#dcfce7', borderLeft: '3px solid #22c55e' }}></div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('teacher.calendar.legendCompleted')}</span>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#fce7f3', borderLeft: '3px solid #ec4899' }}></div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('teacher.calendar.legendOnline')}</span>
                    </div>
                </Col>
            </Row>

            <Modal show={!!selectedEvent} onHide={() => setSelectedEvent(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold' style={{ fontSize: '18px' }}>
                        {selectedEvent?.groupName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='fw-semibold mb-1' style={{ fontSize: '15px' }}>
                        {selectedEvent?.startTime} - {selectedEvent?.endTime}
                    </p>
                    <p className='text-muted mb-3' style={{ fontSize: '13px' }}>{selectedEvent?.date}</p>
                    {selectedEvent?.courseName && (
                        <p className='mb-1' style={{ fontSize: '14px' }}><strong>{t('teacher.calendar.course')}:</strong> {selectedEvent.courseName}</p>
                    )}
                    {selectedEvent?.classroom && (
                        <p className='mb-1' style={{ fontSize: '14px' }}><strong>{t('teacher.classes.classroom')}</strong> {selectedEvent.classroom}</p>
                    )}
                    {selectedEvent?.center && (
                        <p className='mb-1' style={{ fontSize: '14px' }}><strong>{t('teacher.classes.center')}:</strong> {selectedEvent.center}</p>
                    )}
                    {selectedEvent?.topic && (
                        <p className='mb-1' style={{ fontSize: '14px' }}><strong>{t('teacher.calendar.topic')}:</strong> {selectedEvent.topic}</p>
                    )}
                    <div className='d-flex gap-2 mt-2'>
                        {selectedEvent?.isOnline && (
                            <Badge bg='danger' className='rounded-pill'>{t('global.online')}</Badge>
                        )}
                        {selectedEvent?.isSubstitute && (
                            <Badge bg='info' className='rounded-pill'>{t('teacher.calendar.substitutions')}</Badge>
                        )}
                        {selectedEvent?.state && (
                            <Badge bg='secondary' className='rounded-pill'>{selectedEvent.state}</Badge>
                        )}
                    </div>
                    {selectedEvent?.onlineUrl && (
                        <a href={selectedEvent.onlineUrl} target='_blank' rel='noreferrer' className='btn btn-sm btn-outline-danger rounded-3 mt-3'>
                            <i className='fa-solid fa-video me-1'></i>{t('teacher.classes.jointoclass')}
                        </a>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' className='rounded-3' onClick={() => setSelectedEvent(null)}>
                        {t('global.close')}
                    </Button>
                    {selectedEvent?.id && (
                        <Link to={`/teacher/classes/session/${selectedEvent.id}`} className='btn btn-primary rounded-3'>
                            {t('teacher.dashboard.enterclass')}
                        </Link>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    )
}
